/* ============================================================
   useShare — plan origin + portable export helpers

   The encryption key never leaves the client: shared-plan links carry it only
   in the fragment, and portable files embed it locally. This module remembers
   the origin of a plan opened from Redline so copied feedback can point the
   agent back to it without uploading anything.
   ============================================================ */

import { reactive } from 'vue'
import type { Tab, Comment } from '@/types'
import {
  generateKey,
  encrypt,
  buildHash,
  base64urlEncode,
} from '@/crypto'

/* ---- per-tab publish origin (slug + key) -------------------------------- */

/** Where a plan lives on the server, plus the key that opens it. */
export interface PlanOrigin {
  slug: string
  key: Uint8Array
}

// Keyed by tab.hash (the content hash). The key is held in module memory only,
// never persisted to IndexedDB — it is the secret the whole design protects.
const origins = reactive(new Map<string, PlanOrigin>())

export function setPlanOrigin(tabHash: string, origin: PlanOrigin): void {
  origins.set(tabHash, origin)
}
export function getPlanOrigin(tabHash: string): PlanOrigin | undefined {
  return origins.get(tabHash)
}
export function hasPlanOrigin(tabHash: string): boolean {
  return origins.has(tabHash)
}

/* ---- comment export + source-link building ------------------------------ */

/** The committed comments to bundle/share: drafts dropped, transient fields stripped. */
export function shareableComments(tab: Tab): Comment[] {
  return (tab.comments || [])
    .filter((c) => !c._draft)
    .map((c) => {
      const cc: Comment = { ...c }
      delete cc._draft
      delete cc._editing
      delete cc._editPrev
      delete cc._anchorLost
      delete cc._replying
      delete cc._replyDraft
      return cc
    })
}

/** Rebuild the source plan's `/plan/<slug>#k=<key>` URL locally. */
export async function buildShareUrl(origin: PlanOrigin): Promise<string> {
  const fragment = await buildHash(origin.key)
  return `${location.origin}/plan/${origin.slug}#${fragment}`
}

/* ---- portable HTML download --------------------------------------------- */

/** Derive a download name from the tab's filename: `plan.md` → `plan.redline.html`. */
function portableFilename(filename: string): string {
  const base = (filename || 'plan').replace(/\.(md|markdown|txt)$/i, '').replace(/\s+/g, '-')
  return `${base || 'plan'}.redline.html`
}

/**
 * Assemble a portable, fully-offline HTML snapshot of `tab`: the marker-string
 * reader template with the encrypted plan, key, and comments string-replaced
 * in. The plan is re-encrypted under a fresh key embedded in the file, so the
 * snapshot is self-contained and never references the server.
 *
 * Fetches `/reader-template.html` (same-origin; only the real Worker serves it,
 * not the bare Vite dev server). Returns the HTML and a suggested filename.
 */
export async function buildPortableHtml(tab: Tab): Promise<{ html: string; filename: string }> {
  const key = generateKey()
  const blob = await encrypt(key, new TextEncoder().encode(tab.markdown))
  const payloadB64 = base64urlEncode(blob)
  const fragment = await buildHash(key, shareableComments(tab)) // "k=…&c=…"

  // Cloudflare Assets serves the file at its extensionless path (default
  // html_handling drops `.html`); fall back to the `.html` path for other hosts.
  const template = await fetchTemplate()
  if (!template.includes('---ENCRYPTED_PAYLOAD---')) {
    throw new Error('reader template is missing its payload marker')
  }

  // Function replacements so `$` in the (base64url, $-free) values is never
  // treated as a String.replace special; also replaces exactly one occurrence.
  const html = template
    .replace('---ENCRYPTED_PAYLOAD---', () => payloadB64)
    .replace('---ENCRYPTION_KEY---', () => fragment)

  return { html, filename: portableFilename(tab.filename) }
}

/** Fetch the reader template, preferring the extensionless asset path. */
async function fetchTemplate(): Promise<string> {
  for (const path of ['/reader-template', '/reader-template.html']) {
    const resp = await fetch(path)
    if (!resp.ok) continue
    const text = await resp.text()
    if (text.includes('---ENCRYPTED_PAYLOAD---')) return text
  }
  throw new Error('reader template unavailable')
}

/** Trigger a client-side download of `text` as `filename` (no server round-trip). */
export function downloadTextFile(text: string, filename: string, type = 'text/html'): void {
  const blob = new Blob([text], { type: `${type};charset=utf-8` })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Revoke after the click has been handed off to the browser.
  setTimeout(() => URL.revokeObjectURL(url), 0)
}
